<script setup>
import notification from "@/utils/plugins/NotificationService";
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import Services from "@/api/services/newPresencia.service";
import ResourceService from "@/api/services/resource.service";
import { normalizedIncludes } from "@/utils/textSearch";
import moment from 'moment';

import { computed, h, onMounted, ref, shallowRef } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { usePresenceStore } from "@/app/stores/presenceStore";
import { useUiStore } from "@/app/stores/uiStore";
import { downloadExcelReport } from "@/utils/excelReport";
import { buildPresenceSummaryReport } from "@/utils/presenceExcelReport";
import { reportDifferenceClass } from "@/utils/presenceBalance";
import { defaultPresenceReportRange } from "./presenceReportRange";

// Variables reactivas
// eslint-disable-next-line no-unused-vars
const pickerOptions = {
  firstDayOfWeek: 1
}
const presenceStore = usePresenceStore();
const savedFilters = presenceStore.reportFilters;
const daterange = ref(savedFilters ? [savedFilters.from, savedFilters.to] : [])
const employeeName = ref(savedFilters?.employeeName ?? '')
const statusOptions = [
  { id: 0, name: 'No', value: 0 },
  { id: 1, name: 'Producción', value: 1 },
]
const selectedStatusFilter = ref(savedFilters?.productive ?? 1)
const signings = ref([])
const department = ref(savedFilters?.department ?? null)
const departments = ref([])
// eslint-disable-next-line no-unused-vars
const departmentsOriginal = ref([])
const fechaDesde = ref(null)
const fechaHasta = ref(null)
const allSignings = ref([]);
const showTable = ref(false);
const page = ref(savedFilters?.page ?? 1);
const itemsPerPage = ref(savedFilters?.itemsPerPage ?? 50);
const totalResults = computed(() => signings.value.length);
const paginatedSignings = computed(() => {
  const start = (page.value - 1) * itemsPerPage.value;
  return signings.value.slice(start, start + itemsPerPage.value);
});

const uiStore = useUiStore();

onBeforeRouteLeave(() => {
  presenceStore.setReportFilters({
    employeeName: employeeName.value,
    department: department.value,
    productive: selectedStatusFilter.value,
    from: moment(daterange.value[0]).format('YYYY-MM-DD'),
    to: moment(daterange.value[1]).format('YYYY-MM-DD'),
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  });
  presenceStore.setDateRangeReport([...daterange.value]);
});

const isDownloadingReport = ref(false);
const getReportParams = () => ({
  productive: selectedStatusFilter.value,
  from: moment(daterange.value[0]).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
  to: moment(daterange.value[1]).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
  ...(employeeName.value.trim() && { employeeName: employeeName.value.trim() }),
  ...(department.value && { department: department.value }),
});
const downloadPresenceReport = async () => {
  if (isDownloadingReport.value) return;
  const params = getReportParams();
  const departmentName = departments.value.find(item => item.id === department.value || item["@id"] === department.value)?.name || "Todos los departamentos";
  const filters = [
    params.employeeName || "Todos los empleados",
    departmentName,
    statusOptions.find(item => item.value === params.productive)?.name || "",
  ].join(" · ");
  isDownloadingReport.value = true;
  uiStore.startLoading();
  try {
    const response = await Services.getWorkRecordsReport(params);
    if (!Array.isArray(response?.data?.data)) throw new Error("Informe no disponible");
    const rows = response.data.data.filter(row => row.name && (!params.employeeName || normalizedIncludes(row.name, params.employeeName)));
    if (!rows.length) {
      notification.addNotification({ message: "No hay fichajes según los filtros seleccionados", type: "warning" });
      return;
    }
    await downloadExcelReport(buildPresenceSummaryReport({
      rows, from: params.from, to: params.to, filters, now: moment(),
    }));
  } catch {
    notification.addNotification({ message: "No se ha podido descargar el informe. Inténtalo de nuevo.", type: "error" });
  } finally {
    isDownloadingReport.value = false;
    uiStore.stopLoading();
  }
};

// Icono personalizado para el DatePicker
const customPrefix = shallowRef({
  render() {
    return h('img', {
      src: '/calendar-blue.svg',
      style: 'width: 16px; height: 16px; margin-right: 10px;'
    })
  }
})


// Ciclo de vida
onMounted(() => {
  dateRange()
  fechaDesde.value = moment(daterange.value[0]).toDate()
  fechaHasta.value = moment(daterange.value[1]).toDate()
  updateTable(false)
  getDepartments()
})

// Funciones
// Columnas de tiempo de la tabla (la primera es el empleado y la última las alertas)
const timeColumns = [
  { key: 'hours', label: ['JORNADA DE', 'CONVENIO'], mobile: 'Jornada de convenio' },
  { key: 'totalTime', label: ['TIEMPO', 'TOTAL'], mobile: 'Tiempo total' },
  { key: 'workedTime', label: ['TIEMPO', 'EFECTIVO'], mobile: 'Tiempo efectivo' },
  { key: 'breakTime', label: ['PARADA', 'DESCANSO'], mobile: 'Parada descanso' },
  { key: 'otherPauseTime', label: ['PARADA', 'CONCILIACIÓN'], mobile: 'Parada conciliación' },
  { key: 'extraBreakTime', label: ['DESCANSO', 'EXTRA'], mobile: 'Descanso extra' },
  { key: 'difference', label: ['DIFERENCIA', 'TOTAL'], mobile: 'Diferencia' },
]

const differenceClass = (key, value) => key === 'difference' ? reportDifferenceClass(value) : null

let reportRequest = 0;
const updateTable = async (resetPage = true) => {
  const request = ++reportRequest;
  const params = getReportParams();
  uiStore.startLoading();
  try {
    const response = await Services.getWorkRecordsReport(params);
    if (!Array.isArray(response?.data?.data)) throw new Error("Informe no disponible");
    if (request !== reportRequest) return;
    allSignings.value = response.data.data.filter(row => row.name);
    signings.value = allSignings.value;
    if (employeeName.value) searchByName(false);
    page.value = resetPage ? 1 : Math.min(page.value, Math.max(1, Math.ceil(totalResults.value / itemsPerPage.value)));
    showTable.value = true;
    presenceStore.setReportPresence(signings.value);
    presenceStore.setDateRangeReport(daterange.value);
  } catch {
    notification.addNotification({ message: "No se ha podido obtener el informe de presencia", type: "error" });
  } finally {
    uiStore.stopLoading();
  }
};

// eslint-disable-next-line no-unused-vars
const filterByEmployee = () => {
  updateTable()
}

const dateRange = () => {
  const [defaultFrom, defaultTo] = defaultPresenceReportRange()
  daterange.value = [daterange.value[0] || defaultFrom, daterange.value[1] || defaultTo]
}

const getDepartments = (searchQuery = '') => {
  const params = searchQuery.length > 0 ? { name: searchQuery } : {}
  params['order[name]'] = 'ASC'
  ResourceService.getDepartments(params)
    .then(res => {
      departments.value = res.data['hydra:member']
    })
    .catch(() => {
      notification.addNotification({
        message: 'Error al obtener los departamentos',
        type: 'error'
      })
    })
}

const deshabilitarFechasDesde = (date) => {
  if (fechaHasta.value) {
    return date > fechaHasta.value
  }
  return false
}

const deshabilitarFechasHasta = (date) => {
  if (fechaDesde.value) {
    return date < fechaDesde.value
  }
  return false
}

const manejarCambioDesde = (val) => {
  if (fechaHasta.value && val > fechaHasta.value) {
    fechaHasta.value = null
  }
  daterange.value[0] = fechaDesde.value;
  dateRange();
  updateTable();
}

const manejarCambioHasta = (val) => {
  if (fechaDesde.value && val < fechaDesde.value) {
    fechaDesde.value = null
  }
  daterange.value[1] = fechaHasta.value;
  dateRange();
  updateTable();
}

const searchByName = (resetPage = true) => {
  showTable.value = false;
  if (resetPage !== false) page.value = 1;
  if (employeeName.value || employeeName.value === "") {
    signings.value = [];
    signings.value = allSignings.value.filter(v => normalizedIncludes(v.name, employeeName.value.trim()))
  } else {
    signings.value = allSignings.value;
  }
  showTable.value = true;
}

const onPageSizeChange = () => {
  page.value = 1;
}

</script>

<template>
  <div>
    <HeaderTitle title="Informe de Presencia" />
    <el-card class="card-search w-100 p-5" body-style="padding:0">
      <form @submit.prevent="updateTable">
        <div class="form-item mb-3 row">
          <div class="col-md-4 el-col-xs-24">
            <label class="col-md-12">Nombre</label>
            <el-input clearable v-model="employeeName" placeholder="Nombre" @input="searchByName" class="w-100" />
          </div>
          <div class="col-md-4 el-col-xs-24">
            <label>Departamento</label>
            <el-select v-model="department" placeholder="Todos" clearable filterable @change="updateTable()"
              class="w-100">
              <el-option v-for="item in departments" :key="item.id" :label="item.name" :value="item.id" />
            </el-select>
          </div>
          <div class="col-md-4 el-col-xs-24">
            <label class="col-md-12">Producción</label>
            <el-select id="status" v-model="selectedStatusFilter" label="ddd" @change="updateTable()" class="w-100">
              <el-option v-for="item in statusOptions" :key="item.id" :label="item.name" :value="item.value" />
            </el-select>
          </div>
        </div>
        <div class="row presence-report-filter-row">
          <div class="col-md-7 col-12">
            <div class="row date-range-row">
              <div class="col-md-6 el-col-xs-24 date-picker-presence-report">
                <label>Desde</label>
                <div class="date-picker col-md-12 el-col-xs-24">
                  <el-date-picker v-model="fechaDesde" type="date" placeholder="Desde" class="w-100"
                    :disabled-date="deshabilitarFechasDesde" @change="manejarCambioDesde" format="DD/MM/YYYY"
                    :prefix-icon="customPrefix" />
                </div>
              </div>
              <div class="col-md-6 el-col-xs-24 date-picker-presence-report">
                <label>Hasta</label>
                <div class="date-picker col-md-12 el-col-xs-24">
                  <el-date-picker v-model="fechaHasta" type="date" placeholder="Hasta" class="w-100"
                    :disabled-date="deshabilitarFechasHasta" @change="manejarCambioHasta" format="DD/MM/YYYY"
                    :prefix-icon="customPrefix" />
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-5 col-12">
            <div class="presence-report-actions">
              <button type="submit" class="btn btn_red btn-search presence-search" :disabled="isDownloadingReport">
                BUSCAR
              </button>
              <button type="button" class="btn btn_yellow btn-search presence-download"
                :disabled="isDownloadingReport || uiStore.loading" @click="downloadPresenceReport">
                DESCARGAR INFORME
              </button>
            </div>
          </div>
        </div>
      </form>
    </el-card>

    <el-row>
      <div class="table table-users report-table-view">
        <div v-if="signings && signings.length > 0">
          <table class="presence-report-table">
            <colgroup>
              <col class="col-name">
              <col v-for="col in timeColumns" :key="col.key" class="col-time">
              <col class="col-alert">
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div class="d-flex flex-row align-items-center">
                    <img src="@/assets/icons/user.svg" alt="name-icon" style="width: 2em;">
                    <div>EMPLEADO</div>
                  </div>
                </th>
                <th v-for="col in timeColumns" :key="col.key" class="th-time">
                  <div class="d-flex flex-row align-items-center">
                    <svg id="chronometer1" version="1.1" xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                      style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                      <g>
                        <path
                          d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                                                                    c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                                                                    l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                                                                    C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                                                                    c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                                                                    c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                                                                    s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                                                                    c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                                                                    M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                                                                    S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                                                                    c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                        <path
                          d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                                                                    C124.383,80.339,121.025,76.981,116.883,76.981z" />
                      </g>
                    </svg>
                    <div class="header-label">
                      <span v-for="line in col.label" :key="line">{{ line }}</span>
                    </div>
                  </div>
                </th>
                <th>
                  <div>ALERTAS</div>
                </th>
              </tr>
            </thead>
            <tbody v-if="showTable">
              <tr v-for="(sign, index) in paginatedSignings" :key="sign.id + '-' + index + '-' + sign.name">
                <td>
                  <div class="row-table-image-name-link">
                    <div class="link-name">
                      <router-link :to="{ name: 'employee-presence', params: { id: sign.id } }">
                        {{ sign.name }}
                      </router-link>
                    </div>
                  </div>
                </td>
                <td v-for="col in timeColumns" :key="col.key" class="text-center"
                  :class="differenceClass(col.key, sign[col.key])">
                  {{ sign[col.key] }}
                </td>
                <td class="text-center">
                  <el-tooltip v-if="sign.alerts && sign.alerts.length" placement="left">
                    <template #content>
                      <div v-for="alert in sign.alerts" :key="alert.type" class="alert-tooltip-line">
                        {{ alert.message }}
                      </div>
                    </template>
                    <span class="text-alert-icon" role="alert" aria-label="Alertas">
                      <font-awesome-icon icon="exclamation-triangle" />
                    </span>
                  </el-tooltip>
                  <el-tooltip v-else content="Sin alertas" placement="left">
                    <span class="text-ok-icon" aria-label="Sin alertas">
                      <font-awesome-icon icon="check-circle" />
                    </span>
                  </el-tooltip>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="report-cards-view">
        <div v-if="signings && signings.length > 0">
          <div class="mobile-card-list">
            <template v-for="(sign, index) in paginatedSignings" :key="sign.id + '-' + index + '-' + sign.name">
              <el-card class="card-holiday-item">
                <div class="mobile-card-header">
                  <span class="mobile-card-name">
                    <img src="@/assets/icons/user.svg" alt="name-icon" width="16" class="me-1" />
                    <router-link :to="{ name: 'employee-presence', params: { id: sign.id } }"
                      style="text-decoration: none">
                      {{ sign.name }}
                    </router-link>
                  </span>
                  <el-tooltip v-if="sign.alerts && sign.alerts.length" placement="left">
                    <template #content>
                      <div v-for="alert in sign.alerts" :key="alert.type" class="alert-tooltip-line">
                        {{ alert.message }}
                      </div>
                    </template>
                    <span class="text-alert-icon" role="alert" aria-label="Alertas">
                      <font-awesome-icon icon="exclamation-triangle" />
                    </span>
                  </el-tooltip>
                  <span v-else class="text-ok-icon" aria-label="Sin alertas">
                    <font-awesome-icon icon="check-circle" />
                  </span>
                </div>
                <div class="metric-grid">
                  <div v-for="col in timeColumns" :key="col.key" class="metric">
                    <span class="metric-label">{{ col.mobile }}</span>
                    <span class="metric-value"
                      :class="differenceClass(col.key, sign[col.key])">
                      {{ sign[col.key] }}
                    </span>
                  </div>
                </div>
              </el-card>
            </template>
          </div>
        </div>
        <div v-else>
          <h5>No se han encontrado fichajes según los filtros especificados...</h5>
        </div>
      </div>
    </el-row>
    <SharedElementPagination
      class="pagination"
      v-model:current-page="page"
      v-model:page-size="itemsPerPage"
      :total="totalResults"
      @size-change="onPageSizeChange"
      show-always
    />
  </div>
</template>

<style lang="scss" scoped>

.presence-report-filter-row {
  align-items: flex-end;
  row-gap: 16px;
  .date-picker-presence-report {
    .date-picker {
      display: flex;
      width: 100%;
    }

    :deep(.el-date-editor.el-input) {
      display: flex;
      width: 100%;
      height: 44px;
    }

    :deep(.el-input__wrapper) {
      height: 44px;
      box-sizing: border-box;
    }
  }
}

.presence-report-actions {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;

  .btn {
    flex: 1 1 140px;
    min-width: 0;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: 0 !important;
    height: 44px;
    line-height: 16px;
    padding: 6px 12px;
    font-size: 13px;
    white-space: normal;
  }
  .presence-search, .presence-search:hover {
    color: #fff !important;
  }
  .presence-download, .presence-download:hover {
    color: #474ce6 !important;
  }
  .btn:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}
@media (max-width: 767px) {
  .presence-report-actions {
    flex-direction: column;
    align-items: stretch;

    .btn {
      flex: 0 0 auto;
    }
  }
  .card-search {
    padding: 24px !important;
  }
}

.card-search {
  border-radius: 15px;
  /* background: url('../../assets/icons/bombilla.png'), white;
  background-repeat: no-repeat;
  background-position-x: 100%; */

}

#chronometer1 {
  fill: $bg-blue;
  width: 1.6em;

}

.table-users {
  table {
    thead {
      tr {
        th {
          padding: 0px !important;
          margin: 0px !important;
        }
      }
    }

    tbody {
      td {
        .link-name {
          margin: auto;
          margin-left: 10px;
        }
      }
    }
  }
}

.presence-report-table {
  table-layout: fixed;
  width: 100%;

  thead th,
  thead th div {
    font-size: 12px;
  }

  tbody td {
    white-space: nowrap;
  }

  tbody td:first-child {
    white-space: normal;
  }

  .col-name { width: 18%; }
  .col-time { width: 10.5%; }
  .col-alert { width: 8%; }

  .th-time {
    > div {
      justify-content: center;
    }

    svg {
      flex: 0 0 auto;
      margin-right: 6px;
    }

    .header-label {
      display: flex;
      flex-direction: column;
      line-height: 1.15;
      white-space: nowrap;
    }
  }
}

// En pantallas medianas los iconos de las cabeceras no dejan sitio al texto
@media (max-width: 1599px) {
  .presence-report-table .th-time svg {
    display: none;
  }

  .presence-report-table .th-time .header-label {
    align-items: center;
    text-align: center;
  }
}

.difference-negative {
  color: #d9534f;
  font-weight: 600;
}

.difference-positive {
  color: #28a745;
  font-weight: 600;
}

.text-ok-icon {
  color: #2e9e5b;
  font-size: 18px;
}

.text-alert-icon {
  color: #d9534f;
  font-size: 18px;
  cursor: pointer;
}

.alert-tooltip-line {
  max-width: 320px;
}

.text-alert {
  font-size: 11px;
  color: black;
  font-weight: 550;
  text-decoration: none;
}


@media(min-width: 768px) {
  .card-search {
    min-height: 250px;
  }

  // .btn_search {
  //   margin-left: 10px;
  // }

}

@media(max-width:767px) {

  // Los date pickers se apilan en columna y ocupan todo el ancho
  .date-range-row {
    flex-direction: column;

    .date-picker-presence-report {
      width: 100%;
      max-width: 100%;
      margin-bottom: 12px;

      // El componente de Element Plus ocupa todo el ancho
      .el-date-editor {
        width: 100% !important;
      }

      .date-picker {
        width: 100%;
        max-width: 100%;
      }
    }
  }

  // El select de Element Plus también a ancho completo
  .el-select {
    width: 100%;
  }

  // Botón buscar centrado en móvil
  .btn-search {
    width: 100%;
    margin-top: 8px;
  }

  // Reajusta el margen negativo del botón que tenías en inline style
  .d-flex.justify-content-end {
    margin-bottom: 0 !important;
    margin-top: 12px !important;
    justify-content: center !important;
  }

  .text-header {
    margin-top: 0px !important;
    font-size: 2em;
  }

  .buttons-container {
    flex-wrap: wrap;
  }
}

// Tabla en pantallas anchas; tarjetas cuando la tabla ya no cabe
@media (min-width: 900px) {
  .report-cards-view {
    display: none;
  }
}

@media (max-width: 899px) {
  .report-table-view {
    display: none;
  }

  .report-cards-view {
    width: 100%;

    .mobile-card-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 16px;
      width: 100%;
      margin-top: 16px;
    }

    .card-holiday-item {
      width: 100%;
      padding: 10px 0px;
      border-radius: 16px;
      font-size: 15px;

      .holiday-item-span {
        color: $bg-blue;
        font-weight: 800;
        font-size: 16px;
        line-height: 100%;
        letter-spacing: -0.05em;
      }

      .mobile-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding-bottom: 8px;
        margin-bottom: 10px;
        border-bottom: 1px solid #e6e6f2;
      }

      .mobile-card-name {
        display: flex;
        align-items: center;
        font-weight: 800;
        font-size: 15px;
        line-height: 1.2;
        color: $bg-blue;
      }

      .metric-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
        gap: 10px 12px;
      }

      .metric {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .metric-label {
        color: $bg-blue;
        font-weight: 700;
        font-size: 11px;
        line-height: 1.1;
        text-transform: uppercase;
        letter-spacing: 0.02em;
      }

      .metric-value {
        font-size: 16px;
        font-weight: 600;
        line-height: 1.2;
        white-space: nowrap;
      }
    }
  }
}
</style>
